
import { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import ProjectTable from "./components/ProjectTable";
import UploadModal from "./components/UploadModal";
import AddProjectForm from "./components/AddProjectForm";
import { exportToExcel, exportToPDF } from "../../../utils/exportUtil";
import { FaPlus, FaFileExcel, FaSearch, FaFilePdf } from "react-icons/fa";
import { API_BASE_URL } from "../../../config";

const LIMIT = 50;

const Tables = () => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [projects, setProjects] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [adminPermissions, setAdminPermissions] = useState({
    project: { add: false, edit: false, delete: false, upload: false }
  });
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // Fetch admin permissions
  const fetchAdminPermissions = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/get-admin`, // Adjust this endpoint as per your API
        { headers: { token } }
      );
      
      if (response.data.success) {
        const adminData = response.data.result.admin;
        // console.log(adminData)
        setAdminPermissions(adminData.permissions);
        setIsSuperAdmin(adminData.isSuperAdmin || false);
      }
    } catch (error) {
      // console.error("Error fetching admin permissions:", error);
      // Set default permissions on error
      setAdminPermissions({
        project: { add: false, edit: false, delete: false, upload: false }
      });
    }
  };

  const fetchProjects = async (pageNum = page) => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/project/get-all-projects?page=${pageNum}&limit=${LIMIT}`,
        { headers: { token } }
      );
      if (response.data.success) {
        setProjects(response.data.result);
        setTotalPages(response.data.totalPages || 1);
        setTotalCount(response.data.totalCount || 0);
        setPage(pageNum);
      } else {
        toast.error("No projects found.");
      }
    } catch (error) {
      toast.error("Error fetching projects");
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    fetchProjects(newPage);
  };

  const fetchAllProjectsForExport = async () => {
    const token = localStorage.getItem("token");
    const res = await axios.get(
      `${API_BASE_URL}/api/project/get-all-projects?page=1&limit=99999`,
      { headers: { token } }
    );
    if (res.data.success) return res.data.result;
    return [];
  };

  useEffect(() => {
    fetchAdminPermissions();
    fetchProjects();
  }, []);

  // Permission check helper functions
  const hasPermission = (action) => {
    return isSuperAdmin || (adminPermissions.project && adminPermissions.project[action]);
  };

  const handleRestrictedAction = (action, callback) => {
    if (!hasPermission(action)) {
      toast.error(`You don't have permission to ${action} projects`);
      return;
    }
    callback();
  };

  const handleExportExcel = async () => {
    const loadingToast = toast.loading("Preparing Excel export...");
    try {
      const allData = await fetchAllProjectsForExport();
      const cleanedData = allData.map(
        ({ _id, status, createdAt, updatedAt, __v, ...rest }) => rest
      );
      toast.dismiss(loadingToast);
      exportToExcel(cleanedData, "projects.xlsx");
    } catch {
      toast.dismiss(loadingToast);
      toast.error("Export failed");
    }
  };

  const handleExportPDF = async () => {
    const loadingToast = toast.loading("Preparing PDF export...");
    try {
      const allData = await fetchAllProjectsForExport();
      const cleanedData = allData.map(
        ({
          _id, electricitySection, superBuiltUp, carpetArea, frontRoadSize,
          status, createdAt, mainRoadName, distanceFromMainRoad,
          nearestStationName, distanceFromRailwayStation, nearestBusStopName,
          distanceFromBusStop, distanceFromLocalMarket, updatedAt, __v,
          ...rest
        }) => rest
      );
      toast.dismiss(loadingToast);
      exportToPDF(cleanedData, "projects.pdf", "projects");
    } catch {
      toast.dismiss(loadingToast);
      toast.error("Export failed");
    }
  };

  return (
    <div className="p-4">
      {/* Top Bar */}
      <div className="mb-4 flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
        <div>
          <h1 className="text-xl font-bold text-gray-800 dark:text-white">
            Project Management
          </h1>
          {totalCount > 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Total: {totalCount} projects &nbsp;|&nbsp; Page {page} of {totalPages}
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-3">
          <div className="relative">
            <input
              type="text"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="rounded border px-3 py-2 pr-10 text-sm outline-none dark:bg-navy-700 dark:text-white"
            />
            <FaSearch className="absolute right-3 top-2.5 text-gray-500" />
          </div>

          {/* Upload Excel Button */}
          <button
            onClick={() => handleRestrictedAction('upload', () => setShowUploadModal(true))}
            disabled={!hasPermission('upload')}
            className={`flex items-center gap-2 rounded px-4 py-2 text-white transition-all duration-200 ${
              hasPermission('upload')
                ? "bg-green-600 hover:bg-green-700 cursor-pointer"
                : "bg-gray-400 cursor-not-allowed opacity-60"
            }`}
            title={!hasPermission('upload') ? "You don't have permission to upload files" : "Upload Excel file"}
          >
            <FaFileExcel /> Upload Excel
          </button>

          {/* Export PDF Button */}
          <button
            onClick={handleExportPDF}
            className="flex items-center gap-2 rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
          >
            <FaFilePdf /> Export PDF
          </button>

          {/* Export Excel Button */}
          <button
            onClick={handleExportExcel}
            className="flex items-center gap-2 rounded bg-purple-600 px-4 py-2 text-white hover:bg-purple-700"
          >
            <FaFileExcel /> Export Excel
          </button>

          {/* Add Project Button */}
          <button
            onClick={() => handleRestrictedAction('add', () => setShowAddForm(true))}
            disabled={!hasPermission('add')}
            className={`flex items-center gap-2 rounded px-4 py-2 text-white transition-all duration-200 ${
              hasPermission('add')
                ? "bg-indigo-600 hover:bg-indigo-700 cursor-pointer"
                : "bg-gray-400 cursor-not-allowed opacity-60"
            }`}
            title={!hasPermission('add') ? "You don't have permission to add projects" : "Add new project"}
          >
            <FaPlus /> Add Project
          </button>
        </div>
      </div>

      {/* Project Table */}
      <ProjectTable
        search={searchTerm}
        projects={projects}
        onRefresh={() => fetchProjects(page)}
        adminPermissions={adminPermissions}
        isSuperAdmin={isSuperAdmin}
        page={page}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />

      {/* Upload Modal and Add Project Form Modal */}
      {showUploadModal && (
        <UploadModal
          onClose={() => setShowUploadModal(false)}
          onUploadSuccess={fetchProjects}
        />
      )}

      {showAddForm && (
        <AddProjectForm
          onClose={() => setShowAddForm(false)}
          onSuccess={fetchProjects}
        />
      )}
    </div>
  );
};

export default Tables;


